import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@d-environments/environment';
import { ICoreResponse } from '@d-models';
import { CoreTenantInfo } from '@d-models/other/tenant.model';
import { ModalService } from '@d-services/other/modal.service';
import { SSOService } from '@d-services/other/sso.service';
import { AuthService } from '@d-services/wms/auth.service';
import {
  catchError,
  map,
  Observable,
  of,
  ReplaySubject,
  share,
  tap,
  timer,
} from 'rxjs';
import { GetCurrentButtonsPayload } from '../../base/base-approval.abstract';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  baseUrl: string = environment.coreApp.url;
  apiUrl: string = environment.API_URL;
  COUNTRIES_CACHE_TIMEOUT = 60 * 60 * 24 * 1000;
  getCountriesV2$ = this.httpClient
    .get<any>(`${this.apiUrl}/merchant-svc/geography/country/get-all`)
    .pipe(
      share({
        connector: () => new ReplaySubject(1),
        resetOnComplete: () => timer(this.COUNTRIES_CACHE_TIMEOUT),
      }),
    );
  getCountries$ = this.httpClient
    .get<any>(`${this.baseUrl}/Countries/GetAll`, {
      headers: new HttpHeaders({
        Authorization: this.authService.getAuthorizationToken() ?? '',
      }),
    })
    .pipe(
      share({
        connector: () => new ReplaySubject(1),
        resetOnComplete: () => timer(this.COUNTRIES_CACHE_TIMEOUT),
      }),
    );
  cacheState = {};

  constructor(
    private httpClient: HttpClient,
    private authService: AuthService,
    private ssoService: SSOService,
    private modalService: ModalService,
  ) {}

  getStatesByCountry(countryId: string) {
    if (!countryId) {
      return of({ data: [] });
    }
    if (this.cacheState[countryId]) {
      return of(this.cacheState[countryId]);
    }

    return this.httpClient
      .get<any>(
        `${this.apiUrl}/merchant-svc/geography/state/get-all?countryId=` +
          countryId,
      )
      .pipe(
        tap((res) => {
          this.cacheState[countryId] = res;
        }),
      );
  }

  getAllStates() {
    return this.httpClient.get<any>(
      `${this.apiUrl}/merchant-svc/geography/state/get-all`,
    );
  }

  getCountries() {
    return this.getCountriesV2$.pipe();
  }

  getCountry(code: string) {
    return this.getCountriesV2$.pipe(
      map((res) => res.data),
      map((countries) =>
        countries.find((country: any) => country.code === code),
      ),
    );
  }

  getTenantInfo() {
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: this.authService.getAuthorizationToken() ?? '',
        tenantid: this.ssoService.getTenantId(),
      }),
    };
    return this.httpClient.get<ICoreResponse<CoreTenantInfo>>(
      `${this.baseUrl}/Tenants/GetTenantInfo`,
      httpOptions,
    );
  }

  getButtonsInCurrentApprovalFlow(
    payload: GetCurrentButtonsPayload,
  ): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: this.authService.getAuthorizationToken() ?? '',
      }),
    };
    this.modalService.showLoading();
    return this.httpClient
      .post<any>(
        `${this.baseUrl}/ApprovalSettings/GetButtonsInCurrentApprovalFlow`,
        payload,
        httpOptions,
      )
      .pipe(
        catchError((err: any) => {
          return of([]);
        }),
        tap(() => this.modalService.hideLoading()),
      );
  }

  getApplications() {
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: this.authService.getAuthorizationToken() ?? '',
      }),
    };
    return this.httpClient.get<any>(
      `${this.baseUrl}/ProductPackagePlans/GetProductsSubcriptionByPackagePlanId`,
      httpOptions,
    );
  }

  addRole(data: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: this.authService.getAuthorizationToken() ?? '',
      }),
    };
    data.permissions = [data.permissions];
    this.modalService.showLoading();
    return this.httpClient
      .post<any>(`${this.baseUrl}/Roles/Create`, data, httpOptions)
      .pipe(tap(() => this.modalService.hideLoading()));
  }

  updateRole(id: string, data: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: this.authService.getAuthorizationToken() ?? '',
      }),
      params: { id },
    };

    data.permissions = [data.permissions];
    this.modalService.showLoading();
    return this.httpClient
      .put<any>(`${this.baseUrl}/Roles/Update`, data, httpOptions)
      .pipe(tap(() => this.modalService.hideLoading()));
  }

  getRole(id: string) {
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: this.authService.getAuthorizationToken() ?? '',
      }),
      params: { id },
    };

    return this.httpClient
      .get<any>(`${this.baseUrl}/Roles/GetById`, httpOptions)
      .pipe(
        tap((res: any) => {
          res.data.permissions.forEach((permission: any) =>
            permission.modules.forEach((module: any) => {
              module.create = module.createPermission === 'Yes';
              module.read = module.readPermission;
              module.update = module.updatePermission;
              module.delete = module.deletePermission;
              module.print = module.printPermission === 'Yes';
              module.import = module.importPermission === 'Yes';
              module.export = module.exportPermission === 'Yes';
            }),
          );
          return res;
        }),
      );
  }
}
